import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MailIcon, MailCheckIcon } from 'lucide-react';
import ApiClient from '@/shared/services/api';
import { Wordmark } from '@/shared/components/Wordmark';
import { Spinner } from '@/shared/components/Spinner';
import { ThemeToggle } from '@/shared/components/ThemeToggle';

const forgotSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

/**
 * SC-296: asks for the account email and requests a password-reset link. The API never reveals
 * whether the email belongs to an account, so every outcome shows the same confirmation - except
 * a rate-limit rejection, which tells the user to wait rather than claiming an email was sent.
 */
export const ForgotPassword: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotFormData) => {
    setError('');
    try {
      await ApiClient.forgotPassword({ Email: data.email });
      setSent(true);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 429) {
        setError('Too many reset requests. Please wait a few minutes and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12 sm:px-6 lg:px-8">
      {/* Brand-tinted ambient glow (subtle in both themes) */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center">
          <Wordmark className="scale-110" />
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground">
            Reset your password
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            We&apos;ll email you a link to choose a new one
          </p>
        </div>

        <div className="card mt-8 p-8 shadow-modal">
          {sent ? (
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
                <MailCheckIcon className="h-7 w-7 text-success" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">Check your email</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                If an account exists for that email address, we&apos;ve sent a link to reset your
                password. The link expires shortly and can only be used once.
              </p>
              <Link to="/login" className="btn-primary mt-6 w-full">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
              <p className="text-sm text-muted-foreground">
                Enter the email address you use to sign in.
              </p>

              <div>
                <label htmlFor="email" className="label">
                  Email address
                </label>
                <div className="relative mt-1.5">
                  <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    autoComplete="email"
                    className="input pl-9"
                    placeholder="you@company.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-danger">{errors.email.message}</p>
                )}
              </div>

              {error && (
                <div className="rounded-md border border-danger/30 bg-danger-subtle p-3">
                  <p className="text-sm text-danger-foreground">{error}</p>
                </div>
              )}

              <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                {isSubmitting && <Spinner className="text-primary-foreground" />}
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary hover:text-primary/80"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
